/**
 * Predicates that classify GoTrue auth failures so the UI can attach a
 * directed recovery action (see `AuthRecoveryHint`) to the RIGHT errors —
 * instead of leaving every failure as a dead-end red alert.
 *
 * GoTrue surfaces failures inconsistently: sometimes as a stable `code`
 * (e.g. `email_exists`), sometimes only as a human message (e.g.
 * "User already registered", "Invalid login credentials"), because several
 * hooks throw a bare `new Error(message)` that drops the code. These matchers
 * check both.
 */

function errorMessage(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? '');
  }
  return '';
}

function errorCode(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : '';
  }
  return '';
}

/**
 * True when a sign-up failed because the email is already registered.
 * Covers the coded (`email_exists`, `user_already_exists`) and message-only
 * (`User already registered`) shapes.
 */
export function isAccountExistsError(error: unknown): boolean {
  const code = errorCode(error);
  const message = errorMessage(error);

  return (
    code === 'email_exists' ||
    code === 'user_already_exists' ||
    /user already registered/i.test(message) ||
    /already been registered/i.test(message) ||
    /already exists/i.test(message)
  );
}

/**
 * True when a sign-in failed on invalid credentials — the failure mode a
 * user hits when their email is registered with a DIFFERENT method (e.g. a
 * Google account and they typed a password).
 */
export function isInvalidCredentialsError(error: unknown): boolean {
  const code = errorCode(error);
  const message = errorMessage(error);

  return (
    code === 'invalid_credentials' ||
    /invalid login credentials/i.test(message) ||
    /invalid credentials/i.test(message)
  );
}
